// "Trial ended" paywall — State 3 of the design_handoff_account_billing
// handoff. Shown in place of the Billing page's usual content once the
// workspace's trial has run out (or there's no subscription at all), for
// anyone — admin or member; only the admin's "Continue to checkout" button
// actually submits (the backend's CheckoutAPI is admin-only; see
// `canManageBilling`).
//
// Scope right now: this is the "Trial ended" / "none" view only. Active,
// Processing, and the dialogs (Manage seats, Cancel plan, etc.) from the
// handoff are not built yet — this view is reached from Billing.jsx when
// get-access says the workspace can't use RFP Manager, and nothing else in
// that file was touched.
import React, { useEffect, useState } from "react";
import {
  Check,
  Hourglass,
  Loader2,
  Lock,
  Minus,
  Plus,
  Sparkles,
} from "lucide-react";
import { toast } from "../../../utils/appToast";

import ComparePlansTable from "./ComparePlansTable";
import { SALES_URL, fetchRfpPlans, formatMoney, startRfpCheckout } from "../../../services/rfpBillingApi";


const FALLBACK_PLAN = { currency: "USD", unit_amount: 9900, seat_min: 1, seat_max: 10 };

const daysLeft = (isoDate) => {
  if (!isoDate) return 0;
  const diffMs = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
};

export default function TrialEndedCard({ canManageBilling = true, state, trialEndsAt }) {
  const isTrialing = state === "trialing";
  const remaining = isTrialing ? daysLeft(trialEndsAt) : 0;
  const [plan, setPlan] = useState(FALLBACK_PLAN);
  const [seats, setSeats] = useState(1);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchRfpPlans()
      .then((data) => {
        if (!cancelled) setPlan(data);
      })
      .catch(() => {
        /* fall back to FALLBACK_PLAN silently — the catalog rarely changes */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const unitAmount = plan.unit_amount ?? FALLBACK_PLAN.unit_amount;
  const seatMin = plan.seat_min ?? FALLBACK_PLAN.seat_min;
  const seatMax = plan.seat_max ?? FALLBACK_PLAN.seat_max;
  const total = unitAmount * seats;

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const { checkout_url: checkoutUrl } = await startRfpCheckout({
        seats,
        autoRenew: true,
        successUrl: `${origin}/account/billing?billing=success&session_id={CHECKOUT_SESSION_ID}&seats=${seats}`,
        cancelUrl: `${origin}/account/billing?billing=cancelled`,
      });
      if (!checkoutUrl) throw new Error("No checkout URL returned.");
      window.location.href = checkoutUrl;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't open Stripe — try again");
      setCheckingOut(false);
    }
  };

  return (
    <div className="flex flex-col gap-[22px]">
      {/* Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#E9D2E6] bg-[#FBF5FA] px-[18px] py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white text-[#A04A97]">
            <Hourglass size={18} />
          </div>
          <div>
            <p className="text-[16px] font-semibold text-[#1A1A1A]">
              {isTrialing ? "You're on a free trial" : "Your free trial has ended"}
            </p>
            <p className="mt-0.5 max-w-[46ch] text-[14px] text-[#52525B]">
              {isTrialing
                ? "Subscribe anytime to invite teammates and keep access once your trial ends. This covers RFP Manager — Extractor Studio is billed separately."
                : "Choose a plan to keep your projects, knowledge base and proposals. This covers RFP Manager — Extractor Studio is billed separately."}
            </p>
          </div>
        </div>
        <span className="rounded-full border border-[#E9D2E6] bg-white px-3 py-1 text-[13px] font-medium text-[#893F82]">
          {isTrialing ? `${remaining} day${remaining === 1 ? "" : "s"} left` : "0 days left"}
        </span>
      </div>

      {/* Plan cards — fill the row width, no artificial cap */}
      <div className="grid gap-[18px]" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        {/* Premium */}
        <div
          className="relative flex flex-col gap-4 overflow-hidden rounded-[18px] p-6 text-white"
          style={{
            background:
              "radial-gradient(110% 120% at 100% 0%, rgba(201,94,190,.42), transparent 55%)," +
              "radial-gradient(80% 90% at 0% 100%, rgba(243,144,79,.2), transparent 60%), #2C1338",
            boxShadow: "0 20px 40px -18px rgba(44,19,56,.6)",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-[.06em] text-white/70">Premium</span>
            <span className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[12px] font-medium text-white">
              <Sparkles size={13} /> Most popular
            </span>
          </div>
          <h3 className="text-[22px] font-semibold leading-snug">Win together, faster</h3>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-[48px] font-semibold leading-none tracking-[-0.035em] [font-variant-numeric:tabular-nums]">
                {formatMoney(unitAmount)}
              </span>
              <span className="text-[14.5px] text-white/72">per user / month</span>
            </div>
            <p className="mt-1 text-[13.5px] text-white/72">Billed monthly in USD · cancel anytime</p>
          </div>
          <div className="h-px w-full" style={{ background: "linear-gradient(90deg, rgba(255,255,255,.2), transparent)" }} />
          <ul className="flex flex-col gap-2 text-[15px]">
            {["1–10 users", "All RFP Manager features", "Priority email support"].map((line) => (
              <li key={line} className="flex items-center gap-2">
                <Check size={14} className="shrink-0 text-[#E7B6E1]" /> {line}
              </li>
            ))}
          </ul>

          <div className="rounded-xl border border-white/[.14] bg-white/[.06] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[14.5px] font-medium">Users</p>
                <p className="text-[13px] text-white/70 [font-variant-numeric:tabular-nums]">
                  {formatMoney(unitAmount)} × {seats}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Remove a seat"
                  disabled={seats <= seatMin}
                  onClick={() => setSeats((n) => Math.max(seatMin, n - 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white disabled:opacity-30"
                >
                  <Minus size={15} />
                </button>
                <span className="w-5 text-center text-[18px] font-semibold [font-variant-numeric:tabular-nums]">{seats}</span>
                <button
                  type="button"
                  aria-label="Add a seat"
                  disabled={seats >= seatMax}
                  onClick={() => setSeats((n) => Math.min(seatMax, n + 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white disabled:opacity-30"
                >
                  <Plus size={15} />
                </button>
              </div>
            </div>
            {seats >= seatMax && (
              <p className="mt-2 text-[13px] text-white/60">
                Need 11+ users?{" "}
                <a href={SALES_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-white underline underline-offset-2">
                  Talk to sales
                </a>
              </p>
            )}
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 text-[15px]">
              <span className="text-white/70">Total per month</span>
              <span className="font-semibold [font-variant-numeric:tabular-nums]">{formatMoney(total)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCheckout}
            disabled={!canManageBilling || checkingOut}
            className="mt-1 flex items-center justify-center gap-2 rounded-[9px] bg-white py-2.5 text-[16px] font-semibold text-[#2C1338] transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            {checkingOut ? (
              <>
                <Loader2 size={17} className="animate-spin" /> Opening Stripe…
              </>
            ) : (
              "Continue to checkout →"
            )}
          </button>
          {!canManageBilling && (
            <p className="text-center text-[13px] text-white/60">Ask the workspace owner to choose a plan.</p>
          )}
          <p className="flex items-center justify-center gap-1.5 text-[13px] text-white/60">
            <Lock size={12} /> Secure checkout by Stripe
          </p>
        </div>

        {/* Enterprise */}
        <div className="flex flex-col gap-4 rounded-[18px] border border-[#ECE9EE] bg-white p-6">
          <h3 className="text-[22px] font-semibold leading-snug text-[#1A1A1A]">Scale across the org</h3>
          <div>
            <span className="text-[30px] font-semibold text-[#1A1A1A]">Custom</span>
            <p className="mt-1 text-[13.5px] text-[#8A8A90]">Tailored pricing for 11+ users</p>
          </div>
          <ul className="flex flex-col gap-2 text-[15px] text-[#1A1A1A]">
            {[
              "Everything in Premium",
              "SSO (SAML / OIDC) and CRM integrations",
              "99.9% uptime SLA",
              "24/7 critical support",
              "Dedicated CSM and guided onboarding",
            ].map((line) => (
              <li key={line} className="flex items-center gap-2">
                <Check size={15} className="shrink-0 text-[#A04A97]" /> {line}
              </li>
            ))}
          </ul>
          <a
            href={SALES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto flex items-center justify-center rounded-[9px] border border-[#E9D2E6] py-2.5 text-[16px] font-semibold text-[#893F82]"
          >
            Talk to sales
          </a>
        </div>
      </div>

      <ComparePlansTable />
    </div>
  );
}
